import { Injectable } from '@angular/core'

import { cloneDeep, isArray } from 'lodash'
import { DateService, environment } from '@lla-platform/core/core-util'
import { FilterStateKeys, PeriodType, SetFilters } from '@lla-platform/core/core-data-access'
import {
  New_AggregatorType,
  INew_PaginationFilteredRequest,
  INew_TableAction,
  INew_TableFIlterNotifier,
  INew_TableFilter,
  INew_TableFilterRule,
  New_TableColumnType,
  New_TableFilters,
  New_TableOperator
} from '../interfaces/table-filter.interface'
import { Sort } from '@angular/material/sort'
import { IColumnsVisibility } from '../interfaces/advanced-table.interface'
import { Store } from '@ngxs/store'
import moment from 'moment'

@Injectable({
  providedIn: 'root'
})
export class TableActionsService {
  constructor(
    private dateService: DateService,
    private store: Store
  ) {}

  createRequestPayload(info?: INew_TableAction): INew_PaginationFilteredRequest {
    const paylad: INew_PaginationFilteredRequest = {
      filters: {},
      top: environment.pageSize,
      skip: ((info?.page ?? 1) - 1) * environment.pageSize
    }

    if (info?.sort && info.sort.active) {
      paylad['orderBy'] = [
        {
          direction: info.sort.direction === 'asc' ? 'Ascending' : 'Descending',
          property: info.sort.active
        }
      ]
    }

    paylad.filters = Object.fromEntries(
      Object.keys(info?.filters ?? [])
        .map((el) => info?.filters?.[el])
        .filter((el) => el?.items && el.items.length > 0)
        .map((el) => {
          if (!el) {
            return []
          }
          let aggregator = el.aggregator
          let items: INew_TableFilterRule[] | undefined
          switch (el.columnType) {
            case New_TableColumnType.checkbox:
              if (isArray(el.items?.[0].value)) {
                items = (el.items[0].value ?? []).map((value: string) => ({
                  operator: New_TableOperator.Equals,
                  value
                }))
              } else {
                items = []
              }
              break

            case New_TableColumnType.date:
              aggregator = New_AggregatorType.And
              items = el.items?.map((el) => ({
                operator: el.operator,
                value: el.value ?? PeriodType.Custom,
                extraValue: {
                  from: moment(el.extraValue.from, 'YYYY-MM-DD', true).isValid()
                    ? el.extraValue.from
                    : this.dateService.momentToDateString(el.extraValue.from),
                  to: moment(el.extraValue.to, 'YYYY-MM-DD', true).isValid()
                    ? el.extraValue.to
                    : this.dateService.momentToDateString(el.extraValue.to)
                }
              }))
              break

            default:
              items = el.items?.map((el) => ({
                operator: el.operator,
                value: `${el.value}`
              }))
              break
          }
          return [
            el.key,
            {
              key: el.key,
              aggregator,
              items
            }
          ]
        })
    )

    return paylad
  }

  // Rebuilds a page's table state from a saved FiltersState snapshot: merges saved filters into the
  // page's default filter definitions (keeping in-memory listItems, which are stripped on save) and
  // applies a default sort when the snapshot has none.
  restoreFilters(payload: {
    savedActions?: INew_TableAction
    tableFilters: New_TableFilters
    defaultSort?: Sort
  }): { tableActions: INew_TableAction; tableFilters: New_TableFilters } {
    const tableFilters = { ...payload.tableFilters }
    const tableActions = cloneDeep(payload.savedActions) ?? { page: 1 }
    Object.values(tableActions.filters ?? {}).forEach((filter) => {
      if (!filter) {
        return
      }
      tableFilters[filter.key] = {
        ...tableFilters[filter.key],
        ...filter,
        listItems: tableFilters[filter.key]?.listItems
      }
    })
    tableActions.filters = { ...tableFilters }
    if (!tableActions.sort && payload.defaultSort) {
      tableActions.sort = payload.defaultSort
    }
    return { tableActions, tableFilters }
  }

  // The filters the user applied (the ones with items), optionally limited to `keys`; deep-copied so a
  // drill-down link can carry them without sharing state with the page.
  activeFilters(filters?: New_TableFilters, keys?: string[]): New_TableFilters {
    const result: New_TableFilters = {}
    ;(keys ?? Object.keys(filters ?? {})).forEach((key) => {
      const filter = filters?.[key]
      if (filter?.items && filter.items.length > 0) {
        result[key] = cloneDeep(filter)
      }
    })
    return result
  }

  resetFilter(filter: INew_TableFilter): INew_TableFilter {
    return { ...filter, items: undefined, aggregator: undefined }
  }

  prepareChangedFilters(payload: {
    key?: string
    info?: INew_TableFIlterNotifier
    tableActions: INew_TableAction
    tableFilters: New_TableFilters
  }): INew_TableAction | undefined {
    if (!payload.key && !payload.info) {
      return this.clearAllFilters({
        tableActions: payload.tableActions,
        tableFilters: payload.tableFilters
      })
    }

    const { key, info, tableActions, tableFilters } = payload
    if (!info || !key || key === '') {
      return
    }
    if (info.shouldDelete) {
      tableFilters[key] = this.resetFilter(tableFilters[key])
    }
    if (info.filter) {
      tableFilters[key] = { ...info.filter }
    }

    if (info.sort !== undefined) {
      tableActions.sort = info.sort
    }
    if (!info.sort) {
      tableActions.page = 1
    }
    return { ...tableActions, filters: tableFilters }
  }

  clearAllFilters(payload: {
    tableActions: INew_TableAction
    tableFilters: New_TableFilters
  }): INew_TableAction | undefined {
    const { tableActions, tableFilters } = payload
    Object.keys(tableFilters).forEach((key) => {
      const filter = tableFilters[key]
      let shouldReset = false
      if (filter.columnType === New_TableColumnType.number && filter.items && filter.items.length > 1) {
        shouldReset = true
      } else if (
        filter.columnType === New_TableColumnType.text &&
        filter.items &&
        (filter.items.length > 1 ||
          (filter.items.length === 1 && filter.items[0].operator !== New_TableOperator.StartsWith))
      ) {
        shouldReset = true
      }
      if (shouldReset) {
        tableFilters[key] = this.resetFilter(tableFilters[key])
      }
    })
    tableActions.page = 1
    return { ...tableActions, filters: tableFilters }
  }

  // Applies a saved columns visibility/order map to a list's columns. Returns the ordered
  // `displayedColumns` (drives the table column order, visible-only) and the `tableFilters` reordered to
  // match (so the Columns menu reflects the saved order). Columns missing from the saved map keep their
  // definition order and default to visible; filters not present in `allColumns` (e.g. hidden helper
  // columns) are preserved at the end.
  applyColumnsVisibility(payload: {
    visibility: IColumnsVisibility
    allColumns: string[]
    tableFilters: New_TableFilters
  }): { displayedColumns: string[]; tableFilters: New_TableFilters } {
    const { visibility, allColumns, tableFilters } = payload
    const savedOrder = Object.keys(visibility).filter((el) => allColumns.includes(el))
    const newColumns = allColumns.filter((el) => !savedOrder.includes(el))
    const orderedColumns = [...savedOrder, ...newColumns]
    const displayedColumns = orderedColumns.filter((el) => (el in visibility ? visibility[el] : true))
    const extraKeys = Object.keys(tableFilters).filter((el) => !orderedColumns.includes(el))
    const orderedFilters = Object.fromEntries(
      [...orderedColumns, ...extraKeys].map((el) => [el, tableFilters[el]])
    ) as New_TableFilters
    return { displayedColumns, tableFilters: orderedFilters }
  }

  saveFilters(tableActions: INew_TableAction, key: FilterStateKeys) {
    const saveTableAction = cloneDeep(tableActions)
    if (saveTableAction.filters) {
      for (const key in saveTableAction.filters) {
        const filter = saveTableAction.filters[key]
        saveTableAction.filters[key] = { ...filter, listItems: undefined }
      }
    }
    this.store.dispatch(new SetFilters({ [key]: saveTableAction }))
  }

  matchesNumberRule(value: number, rule: INew_TableFilterRule): boolean {
    const target = Number(rule.value)
    if (isNaN(target)) {
      return true
    }
    switch (rule.operator) {
      case New_TableOperator.LessThan:
        return value < target
      case New_TableOperator.LessThanOrEqual:
        return value <= target
      case New_TableOperator.Equals:
        return value === target
      case New_TableOperator.NotEquals:
        return value !== target
      case New_TableOperator.GreaterThan:
        return value > target
      case New_TableOperator.GreaterThanOrEqual:
        return value >= target
      default:
        return true
    }
  }
}
